<!-- biome-ignore-all lint/a11y/noNoninteractiveElementToInteractiveRole: ul[role=listbox] > li[role=option] is the ARIA listbox pattern -->
<template>
  <div class="internal-data-table__rows-selector">
    <div
      class="rows-input__wrapper"
      role="button"
      tabindex="0"
      :aria-expanded="showList"
      @click="showList=!showList"
      @keydown.enter="showList=!showList"
      @keydown.space.prevent="showList=!showList"
    >
      <div class="rows-input">
        {{ rowsComputed }}
      </div>
      <div class="triangle"></div>
    </div>
    <ul
      class="select-items"
      :class="{show: showList, inside: showInsideOfTable}"
      role="listbox"
    >
      <li
        v-for="item in rowsItems"
        :key="item"
        :class="{selected: item === rowsComputed }"
        role="option"
        :aria-selected="item === rowsComputed"
        tabindex="0"
        @click="changeSelectedRows(item)"
        @keydown.enter="changeSelectedRows(item)"
        @keydown.space.prevent="changeSelectedRows(item)"
      >
        {{ item }}
      </li>
    </ul>
  </div>
</template>

<script lang="ts" setup>
  import {
    ref,
    computed,
    type PropType,
    onMounted,
    onBeforeUnmount,
    inject,
    watch,
    type Ref,
  } from 'vue';

  const props = defineProps({
    modelValue: { type: Number, required: true },
    rowsItems: { type: Array as PropType<number[]>, required: true },
  });

  const emits = defineEmits(['update:modelValue']);

  const showList = ref(false);

  const showInsideOfTable = ref(false);
  const dataTable = inject('dataTable') as Ref<HTMLDivElement>;
  watch(showList, (val) => {
    if (val && dataTable) {
      const windowHeight = window.innerHeight;
      const dataTableHeight = dataTable.value.getBoundingClientRect().height;
      const dataTableTop = dataTable.value.getBoundingClientRect().top;
      if (windowHeight - (dataTableHeight + dataTableTop) <= 100) {
        showInsideOfTable.value = true;
      } else {
        showInsideOfTable.value = false;
      }
    }
  });

  const rowsComputed = computed({
    get: (): number => props.modelValue,
    set: (value: number): void => {
      emits('update:modelValue', value);
    },
  });

  const changeSelectedRows = (value: number) => {
    rowsComputed.value = value;
    showList.value = false;
  };

  // Click outside to close rows selector
  // @ts-expect-error
  const isDescendant = (child, className) => {
    let node = child.parentNode;
    while (node != null) {
      if (node.classList?.contains(className)) {
        return true;
      }
      node = node.parentNode;
    }
    return false;
  };

  // @ts-expect-error
  const closeRowsSelector = (e) => {
    if (!isDescendant(e.target, 'internal-data-table__rows-selector'))
      showList.value = false;
  };

  onMounted(() => {
    document.addEventListener('click', closeRowsSelector);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', closeRowsSelector);
  });

  const themeColor = inject('themeColor');
</script>

<style scoped>
  .internal-data-table__rows-selector {
    display: inline-block;
    min-width: 45px;
    position: relative;
    margin: 0px 10px;
    width: var(--internal-table-rows-per-page-selector-width);
  }
  .internal-data-table__rows-selector .rows-input__wrapper {
    height: 20px;
    border-bottom: 1px solid var(--internal-table-footer-font-color);
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0px 5px;
    cursor: pointer;
  }
  .internal-data-table__rows-selector .rows-input__wrapper .triangle {
    display: inline-block;
    vertical-align: middle;
    width: 0px;
    height: 0px;
    border-top: solid 6px var(--internal-table-footer-font-color);
    border-left: solid 6px transparent;
    border-right: solid 6px transparent;
  }
  .internal-data-table__rows-selector ul.select-items.show {
    display: block;
  }
  .internal-data-table__rows-selector ul.select-items.inside {
    bottom: 0px;
    top: auto;
  }
  .internal-data-table__rows-selector ul.select-items {
    position: absolute;
    top: 20px;
    left: 0px;
    width: 100%;
    display: none;
    margin: 0px;
    padding: 0px;
    text-align: left;
    list-style-type: none;
    box-shadow:
      0 5px 5px -3px rgba(0, 0, 0, 0.2),
      0 8px 10px 1px rgba(0, 0, 0, 0.14),
      0 3px 14px 2px rgba(0, 0, 0, 0.12);
    z-index: var(--internal-table-rows-per-page-selector-z-index);
  }
  .internal-data-table__rows-selector ul.select-items li {
    cursor: pointer;
    padding: var(--internal-table-rows-per-page-selector-option-padding);
    background-color: var(--internal-table-footer-background-color);
  }
  .internal-data-table__rows-selector ul.select-items li.selected {
    color: #fff;
    background-color: v-bind(themeColor);
  }
</style>
